import { useEffect, useState } from "react";
import axios from "axios";
import { Building2 } from "lucide-react";
import Reveal from "./Reveal";
import { responsiveSrcSet } from "../lib/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ClientsGrid = () => {
  const [clients, setClients] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/clients`);
        if (!cancelled) setClients(res.data || []);
      } catch (_e) {
        if (!cancelled) setClients([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!clients.length) return null;

  return (
    <section
      data-testid="clients-grid"
      className="border-b border-black/5 bg-white py-24"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal className="mb-14 flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <div>
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / 02 Builders who trust us
            </div>
            <h2 className="mt-4 font-display text-4xl uppercase leading-[0.92] tracking-tighter text-[#1c1c1e] md:text-6xl">
              Built into the
              <br />
              skyline of <span className="text-[#e85a1c]">Hyderabad.</span>
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-[#4a4a4e]">
            From mid-rise residences to landmark commercial towers — the
            steel inside has come through our yard. Trusted by over 500
            projects across the twin cities.
          </p>
        </Reveal>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {clients.map((c, i) => {
            const card = (
              <div
                data-testid={`client-card-${c.id}`}
                className="group flex h-full flex-col items-center gap-3 rounded-xl border border-black/5 bg-[#f7f7f5] p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[#e85a1c]/40 hover:bg-white hover:shadow-lux-hover"
              >
                {/* Logo box */}
                <div className="flex h-20 w-full items-center justify-center overflow-hidden rounded-lg bg-white shadow-lux transition-colors group-hover:bg-white">
                  {c.logo_url ? (
                    <img
                      src={c.logo_url}
                      srcSet={responsiveSrcSet(c.logo_url, [160, 240, 320])}
                      sizes="(min-width: 1024px) 200px, (min-width: 640px) 22vw, 44vw"
                      alt={c.company_name}
                      loading="lazy"
                      decoding="async"
                      className="max-h-16 max-w-full object-contain p-1"
                    />
                  ) : (
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#fff3eb] text-[#e85a1c] transition-colors group-hover:bg-[#e85a1c] group-hover:text-white">
                      <Building2 size={20} />
                    </span>
                  )}
                </div>
                {/* Name */}
                <div className="flex flex-col items-center gap-1">
                  <div className="font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div
                    className="line-clamp-2 text-[12.5px] font-semibold leading-snug text-[#1c1c1e]"
                    title={c.company_name}
                  >
                    {c.company_name}
                  </div>
                  {c.project_count ? (
                    <div className="font-mono-industrial text-[9px] uppercase tracking-[0.2em] text-[#e85a1c]">
                      {c.project_count} project{c.project_count === 1 ? "" : "s"}
                    </div>
                  ) : null}
                </div>
              </div>
            );

            const wrap = c.website_url ? (
              <a
                href={c.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-full"
                data-testid={`client-link-${c.id}`}
              >
                {card}
              </a>
            ) : (
              card
            );

            return (
              <Reveal key={c.id} delay={Math.min(i * 25, 400)} className="h-full">
                {wrap}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ClientsGrid;
